import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IAccount } from 'app/store/models/account.model';
import { IListApiModel } from 'app/store/models/api.model';
import { ICorrectionParams, IManualRevenueLedgerParams, IReferralPartner } from 'app/store/models/franchisee.model';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FranchiseeService {

  constructor(
    private readonly httpClient: HttpClient
  ) { }

  listFranchisees(): Observable<IListApiModel<IAccount>> {
    return this.httpClient.get<IListApiModel<IAccount>>(`${environment.apiUrl}/v1/users`, {
      params: {
        user_type: 'franchisee'
      }
    });
  }

  listReferralPartners(params?: {
    franchisee_id?: number;
    status?: string;
    page_size?: number;
    paginated?: boolean;
  }): Observable<IListApiModel<IReferralPartner>> {
    const httpParams: Record<string, string> = {};
    if (params?.franchisee_id != null) {
      httpParams.franchisee_id = String(params.franchisee_id);
    }
    if (params?.status) {
      httpParams.status = params.status;
    }
    if (params?.page_size != null) {
      httpParams.page_size = String(params.page_size);
    }
    if (params?.paginated != null) {
      httpParams.paginated = String(params.paginated);
    }
    return this.httpClient.get<IListApiModel<IReferralPartner>>(`${environment.apiUrl}/v1/referralpartners`, {
      params: httpParams,
    });
  }

  listActiveReferralPartners(franchiseeId: number): Observable<IListApiModel<IReferralPartner>> {
    return this.listReferralPartners({
      franchisee_id: franchiseeId,
      status: 'active',
      page_size: 500,
      paginated: false,
    });
  }

  createReferralPartner(data: {name: string, contact_email: string, franchisee_id?: number, referral_partner_share: number, send_docusign?: boolean}): Observable<any> {
    return this.httpClient.post(`${environment.apiUrl}/v1/referralpartners`, data);
  }

  updateReferralPartner(id: number, data: {name: string, contact_email: string, franchisee_id: number, company_name: string, address: string, details: string, notes: string}): Observable<any> {
    return this.httpClient.put(`${environment.apiUrl}/v1/referralpartners/${id}`, data);
  }

  approveReferralPartner(id: number): Observable<any> {
    return this.httpClient.post(`${environment.apiUrl}/v1/referralpartners/${id}/approve`, {});
  }

  resendOnboardingEmail(id: number): Observable<any> {
    return this.httpClient.post(`${environment.apiUrl}/v1/referralpartners/${id}/resend-onboarding`, {});
  }

  resendAgreement(id: number): Observable<any> {
    return this.httpClient.post(`${environment.apiUrl}/v1/referralpartners/${id}/resend-agreement`, {});
  }

  approveDeal(id: number): Observable<any> {
    return this.httpClient.post(`${environment.apiUrl}/v1/deals/${id}/approve`, {});
  }

  updateDeal(id: number, data: {franchisee_share: string, referral_partner_share: string, notes: string, effective_from: string, effective_until: string}): Observable<any> {
    return this.httpClient.put(`${environment.apiUrl}/v1/deals/${id}`, data);
  }

 registerReferral(data: any): Observable<any> {
    return this.httpClient.post(`${environment.apiUrl}/v1/referralpartners/confirm`, data);
  }

  viewReferralPartnerByConfirmationToken(token: string): Observable<IReferralPartner> {
    return this.httpClient.get<IReferralPartner>(`${environment.apiUrl}/v1/referralpartners/${token}`);
  }

  attributeAccounts(
    franchiseeId: number,
    data: {
      account_ids: number[];
      referral_partner_id?: number | null;
      confirm_referral_partner_transfer?: boolean;
    },
  ): Observable<any> {
    const body: Record<string, unknown> = {
      account_ids: data.account_ids,
    };
    if (Object.prototype.hasOwnProperty.call(data, 'referral_partner_id')) {
      body.referral_partner_id = data.referral_partner_id;
    }
    if (data.confirm_referral_partner_transfer) {
      body.confirm_referral_partner_transfer = true;
    }
    return this.httpClient.post(`${environment.apiUrl}/v1/franchisee/${franchiseeId}/attribute`, body);
  }

  updateFranchiseeAttribute(accountId: number, attribute: string, value: any): Observable<any> {
    return this.httpClient.post(`${environment.apiUrl}/v1/franchisee/${accountId}/attribute`, {
      [attribute]: value
    });
  }

  assignFranchiseeAccount(
    franchiseeId: number,
    accountId: number,
    options?: {
      referral_partner_id?: number | null;
      confirm_referral_partner_transfer?: boolean;
    },
  ): Observable<any> {
    return this.attributeAccounts(franchiseeId, {
      account_ids: [accountId],
      ...options,
    });
  }

  removeFranchiseeAssignment(franchiseeId: number, accountId: number): Observable<any> {
    return this.httpClient.delete(`${environment.apiUrl}/v1/franchisee/${franchiseeId}/attribute/${accountId}`);
  }

  exportRevenueLadger(params: any, format: string): Observable<any> {
    return this.httpClient.get(`${environment.apiUrl}/v1/admin/revenue-reports`, {
      params: {
        ...params,
        export: format
      },
      responseType: 'blob' as 'json'
    });
  }

  disburseRevenueLadger(id: number[]): Observable<any> {
    return this.httpClient.post(`${environment.apiUrl}/v1/revenueledger/disburse`, {
      entry_ids: id
    });
  }

  disburseReferralRevenueLadger(id: number[]): Observable<any> {
    return this.httpClient.post(`${environment.apiUrl}/v1/revenueledger/disburse-referral`, {
      entry_ids: id
    });
  }

  createManualRevenueLedger(data: IManualRevenueLedgerParams): Observable<any> {
    return this.httpClient.post(`${environment.apiUrl}/v1/revenueledger`, data);
  }

  correction(id: number, data: ICorrectionParams): Observable<any> {
    return this.httpClient.post(`${environment.apiUrl}/v1/revenueledger/${id}/correction`, data);
  }

}
